import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ValidCell } from '../types';
import { BUILDINGS } from '../constants';

interface ScoreTooltipProps {
    position: ValidCell;
    x: number;
    y: number;
    currentTeam?: 'A' | 'B';
    teamAPositions: ValidCell[];
    teamBPositions: ValidCell[];
    competitorPositions: ValidCell[];
    cost?: number;
    financeMode?: boolean;
    placement?: {
        vertical: 'top' | 'bottom';
        horizontal: 'left' | 'right' | 'center';
    };
}

const calculatePotentialScore = (
    position: ValidCell,
    team: 'A' | 'B',
    teamAPositions: ValidCell[],
    teamBPositions: ValidCell[],
    competitorPositions: ValidCell[]
): { buildings: Array<{ id: string; baseScore: number; contribution: number; percentage: number }>; total: number } => {
    const affectedBuildings: Array<{ id: string; baseScore: number; contribution: number; percentage: number }> = [];
    let totalScore = 0;

    // Simulate adding this position to the team's positions
    const simulatedTeamAPositions = team === 'A' ? [...teamAPositions, position] : teamAPositions;
    const simulatedTeamBPositions = team === 'B' ? [...teamBPositions, position] : teamBPositions;

    Object.entries(BUILDINGS).forEach(([buildingId, building]) => {
        const adjacentCells = building.adjacent as readonly ValidCell[];
        if (!adjacentCells.includes(position)) return;

        // Count adjacent positions for each team
        let adjA = 0, adjB = 0, adjCompetitor = 0;

        adjacentCells.forEach((pos) => {
            if (simulatedTeamAPositions.includes(pos)) adjA++;
            if (simulatedTeamBPositions.includes(pos)) adjB++;
            if (competitorPositions.includes(pos)) adjCompetitor++;
        });

        const totalInfluence = adjA + adjB + adjCompetitor;
        if (totalInfluence === 0) return;

        // Calculate dynamic bonus based on team count
        let teamCount = 0;
        if (adjA > 0) teamCount++;
        if (adjB > 0) teamCount++;
        if (adjCompetitor > 0) teamCount++;

        let bonus = 0;
        if (teamCount === 1) bonus = 6;
        else if (teamCount === 2) bonus = 3;
        else if (teamCount === 3) bonus = 0;

        const dynamicScore = building.score + bonus;

        // Calculate contribution for current team
        let contribution = 0;
        let percentage = 0;

        if (adjCompetitor > 0) {
            // Three-way sharing
            const teamInfluence = team === 'A' ? adjA : adjB;
            percentage = (teamInfluence / totalInfluence) * 100;
            contribution = dynamicScore * (teamInfluence / totalInfluence);
        } else {
            // Two-way sharing
            if (adjA + adjB === 0) return;

            if (adjA === adjB) {
                percentage = 50;
                contribution = dynamicScore / 2;
            } else if (team === 'A') {
                if (adjA > adjB) {
                    percentage = adjB === 0 ? 100 : 66.67;
                    contribution = adjB === 0 ? dynamicScore : (dynamicScore * 2) / 3;
                } else {
                    percentage = adjA === 0 ? 0 : 33.33;
                    contribution = adjA === 0 ? 0 : dynamicScore / 3;
                }
            } else { // team === 'B'
                if (adjB > adjA) {
                    percentage = adjA === 0 ? 100 : 66.67;
                    contribution = adjA === 0 ? dynamicScore : (dynamicScore * 2) / 3;
                } else {
                    percentage = adjB === 0 ? 0 : 33.33;
                    contribution = adjB === 0 ? 0 : dynamicScore / 3;
                }
            }
        }

        if (contribution > 0) {
            affectedBuildings.push({
                id: buildingId,
                baseScore: building.score,
                contribution: Math.round(contribution * 10) / 10,
                percentage: Math.round(percentage)
            });
            totalScore += contribution;
        }
    });

    return {
        buildings: affectedBuildings.sort((a, b) => b.contribution - a.contribution),
        total: Math.round(totalScore * 10) / 10
    };
};

const ScoreTooltip: React.FC<ScoreTooltipProps> = ({
    position,
    x,
    y,
    currentTeam = 'A',
    teamAPositions,
    teamBPositions,
    competitorPositions,
    cost = 0,
    financeMode = false,
    placement = { vertical: 'top', horizontal: 'center' }
}) => {
    const { t } = useTranslation();
    const scoreData = calculatePotentialScore(
        position,
        currentTeam,
        teamAPositions,
        teamBPositions,
        competitorPositions
    );

    if (scoreData.buildings.length === 0 && !financeMode) {
        return null;
    }

    // Calculate transforms based on placement
    // Horizontal:
    // - left: 0% (anchor left side to x)
    // - center: -50% (anchor center to x)
    // - right: -100% (anchor right side to x)
    const translateX = placement.horizontal === 'left' ? '0%' :
        placement.horizontal === 'right' ? '-100%' :
            '-50%';

    // Vertical:
    // - top: -100% (anchor bottom of tooltip to y)
    // - bottom: 0 (anchor top of tooltip to y) (offset handled via margin or y adjustment)
    // Note: Previous logic used 10px offset here. Let's incorporate it.
    const translateY = placement.vertical === 'top' ? '-100%' : '10px';

    // Adjust y coordinate offset for top placement to clear the cursor/finger
    const topOffset = placement.vertical === 'top' ? -10 : 0;

    return (
        <motion.div
            className="tooltip"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            style={{
                position: 'fixed',
                pointerEvents: 'none',
                left: `${x}px`,
                top: `${y + topOffset}px`,
                x: translateX,
                y: translateY,
                zIndex: 9999,
                transformOrigin: `${placement.horizontal === 'left' ? 'left' : placement.horizontal === 'right' ? 'right' : 'center'} ${placement.vertical === 'top' ? 'bottom' : 'top'}`
            }}
        >
            <div className="tooltip-header flex justify-between items-center">
                <span>{position}</span>
                {financeMode && (
                    <span className="text-xs font-normal ml-2 bg-red-100 text-red-800 px-1 rounded dark:bg-red-900/50 dark:text-red-200">
                        -{cost} {t('scores.credits')}
                    </span>
                )}
            </div>

            <div style={{ marginBottom: 'var(--space-sm)' }}>
                {scoreData.buildings.map((building) => (
                    <div key={building.id} className="tooltip-item">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                color: building.percentage > 50 ? 'var(--success)' :
                                    building.percentage > 30 ? 'var(--warning)' :
                                        'var(--text-secondary)'
                            }}>
                                •
                            </span>
                            <span>{building.id}</span>
                        </span>
                        <span style={{
                            fontWeight: '600',
                            color: 'var(--text-primary)'
                        }}>
                            +{building.contribution} {t('board.points')}
                            <span style={{
                                fontSize: '0.75rem',
                                marginLeft: '4px',
                                opacity: 0.7
                            }}>
                                ({building.percentage}%)
                            </span>
                        </span>
                    </div>
                ))}
            </div>

            <div className="tooltip-total">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('board.tooltip.estimatedTotal')}:</span>
                    <span className="text-gradient" style={{ fontSize: '1.25rem' }}>
                        +{scoreData.total} {t('board.points')}
                    </span>
                </div>
            </div>

            {scoreData.total > 40 && (
                <div style={{
                    marginTop: 'var(--space-sm)',
                    padding: 'var(--space-sm)',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    fontSize: '0.75rem',
                    color: 'var(--success)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <span>🎯</span>
                    <span>{t('board.tooltip.excellentChoice')}</span>
                </div>
            )}
        </motion.div>
    );
};

export default ScoreTooltip;
